/**
 * Create Company Page Component
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../../contexts/AuthContext";
import { useIsDesktop } from "../../hooks/useIsDesktop";
import { ResponsiveLayout } from "../../components/layout";
import { apiClient, getErrorMessage } from "../../utils/api";

const INDUSTRIES = [
  "IT i tehnologija", "Proizvodnja", "Trgovina", "Građevinarstvo", "Ugostiteljstvo",
  "Financije", "Zdravstvo", "Obrazovanje", "Transport", "Poljoprivreda",
  "Turizam", "Nekretnine", "Marketing", "Pravo", "Konzalting", "Ostalo"
];

const CreateCompanyPage = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const isDesktop = useIsDesktop();
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    city: "",
    industry: "",
    oib: "",
    address: "",
    director_name: "",
    phone: "",
    email: ""
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error("Naziv firme je obavezan");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        name: formData.name.trim(),
        city: formData.city.trim() || "Nepoznato",
        industry: formData.industry || "Ostalo",
        oib: formData.oib.trim() || null,
        address: formData.address.trim() || null,
        director_name: formData.director_name.trim() || null,
        phone: formData.phone.trim() || null,
        email: formData.email.trim() || null
      };

      const response = await apiClient.post('/companies', payload);
      
      toast.success("Firma uspješno kreirana!");
      navigate(`/company/${response.data.id}`);
    } catch (error) {
      console.error("Failed to create company:", error);
      toast.error(getErrorMessage(error));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ResponsiveLayout title="Nova firma" showBack onBack={() => navigate(-1)}>
      <div className={isDesktop ? "max-w-2xl" : ""}>
        <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 space-y-4">
          {/* Basic Info */}
          <div className="space-y-3">
            <h3 className="font-medium text-gray-900 text-sm">Osnovni podaci</h3>
            
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Naziv firme *</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                data-testid="company-name-input"
                placeholder="Unesite naziv firme"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B3F8F]/20 focus:border-[#1B3F8F]"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Grad</label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  data-testid="company-city-input"
                  placeholder="Sarajevo"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B3F8F]/20 focus:border-[#1B3F8F]"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Djelatnost</label>
                <select
                  name="industry"
                  value={formData.industry}
                  onChange={handleChange}
                  data-testid="company-industry-select"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B3F8F]/20 focus:border-[#1B3F8F]"
                >
                  <option value="">Odaberi...</option>
                  {INDUSTRIES.map(ind => (
                    <option key={ind} value={ind}>{ind}</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">ID</label>
              <input
                type="text"
                name="oib"
                value={formData.oib}
                onChange={handleChange}
                data-testid="company-oib-input"
                placeholder="12345678901"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B3F8F]/20 focus:border-[#1B3F8F]"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Adresa</label>
              <input
                type="text"
                name="address"
                value={formData.address}
                onChange={handleChange}
                data-testid="company-address-input"
                placeholder="Ulica i broj, poštanski broj"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B3F8F]/20 focus:border-[#1B3F8F]"
              />
            </div>
          </div>

          {/* Contact Info */}
          <div className="space-y-3 pt-2 border-t border-gray-100">
            <h3 className="font-medium text-gray-900 text-sm">Kontakt podaci</h3>
            
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Ime direktora</label>
              <input
                type="text"
                name="director_name"
                value={formData.director_name}
                onChange={handleChange}
                data-testid="company-director-input"
                placeholder="Ime i prezime"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B3F8F]/20 focus:border-[#1B3F8F]"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Telefon</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  data-testid="company-phone-input"
                  placeholder="+387 61 234 567"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B3F8F]/20 focus:border-[#1B3F8F]"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Email</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  data-testid="company-email-input"
                  placeholder="email@firma.ba"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B3F8F]/20 focus:border-[#1B3F8F]"
                />
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={submitting}
            data-testid="create-company-submit"
            className="w-full py-3 bg-[#1B3F8F] text-white rounded-lg font-medium hover:bg-[#152f6b] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? "Kreiram..." : "Kreiraj firmu"}
          </button>
        </form>
      </div>
    </ResponsiveLayout>
  );
};

export default CreateCompanyPage;
